/**
 * O cartão "Lançamentos do mês (Outros Bancos)".
 *
 * Papel: reproduz `#ob-lc-tabela` e `_renderObLcTabela` (l. 550–555,
 * 4039–4065): uma tabela simples, sem a moldura `.tbl`, com Data, Favorecido
 * (e a descrição embaixo), Categoria (e a subcategoria embaixo), Valor com o
 * sinal do tipo, e o 🗑 que pede confirmação.
 *
 * Como usar: quem monta passa os lançamentos na ordem em que a API os devolve,
 * que já é a da data como texto, e `undefined` enquanto a consulta não volta. A
 * linha de "vazio" é também a de espera e a de falha. Quem só tem `ver` não vê
 * o 🗑. Não há edição.
 *
 * O 🗑 de toda linha fica desabilitado enquanto uma exclusão não voltou: o
 * segundo clique perguntaria de novo e mandaria a mesma exclusão, que voltaria
 * com erro.
 */
import { type LancamentoDeOutrosBancos } from '@concierge/shared';

import { useNotificacao } from '@/shared/embutido';
import { formatarData } from '@/shared/lib/formato';
import { cn } from '@/shared/lib/utils';
import { formatarReais } from '../formato';
import { useExcluirLancamentoDeOutrosBancos } from '../hooks/usar-financas-extras';
import { Botao } from './kit/botao';
import { CabecalhoDoCartao, Cartao, CorpoDoCartao, TituloDoCartao } from './kit/cartao';

/** A célula do cabeçalho e das linhas: `padding:6px 10px`. */
const CLASSE_DA_CELULA = 'px-2.5 py-1.5';

/** O texto de baixo de Favorecido e de Categoria: 11 px em `texto-fraco`. */
const CLASSE_DA_SEGUNDA_LINHA = 'text-[11px] text-texto-fraco';

interface TabelaDeOutrosBancosProps {
  /** Os lançamentos do mês; `undefined` enquanto a consulta não volta. */
  lancamentos: readonly LancamentoDeOutrosBancos[] | undefined;
  /** Se a consulta falhou. */
  falhou: boolean;
  /** O "Tentar de novo" da falha. */
  onTentarDeNovo: () => void;
  podeEditar: boolean;
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * O cartão com a tabela do mês.
 *
 * @param lancamentos - Os lançamentos, já em ordem de data.
 * @param falhou - Troca "Carregando…" pela frase de falha.
 * @param onTentarDeNovo - Pede a consulta de novo.
 * @param podeEditar - Falso esconde o 🗑.
 * @returns O cartão.
 */
export function TabelaDeOutrosBancos({
  lancamentos,
  falhou,
  onTentarDeNovo,
  podeEditar,
}: TabelaDeOutrosBancosProps) {
  const notificar = useNotificacao();
  const excluir = useExcluirLancamentoDeOutrosBancos();

  /**
   * O 🗑 da linha (l. 4031–4037): pergunta e exclui, sem aviso de sucesso.
   *
   * @param id - O lançamento.
   */
  async function handleExcluir(id: string) {
    if (!window.confirm('Excluir este lançamento?')) return;
    try {
      await excluir.mutateAsync(id);
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <Cartao>
      <CabecalhoDoCartao>
        <TituloDoCartao>Lançamentos do mês (Outros Bancos)</TituloDoCartao>
      </CabecalhoDoCartao>
      <CorpoDoCartao className="p-0">
        <table className="w-full border-collapse">
          {lancamentos === undefined || lancamentos.length === 0 ? (
            <tbody>
              <tr>
                <td className="p-3.5 text-center text-[12px] text-texto-fraco">
                  {lancamentos !== undefined ? (
                    'Nenhum lançamento neste mês.'
                  ) : falhou ? (
                    <>
                      Não foi possível carregar.{' '}
                      <Botao variante="fantasma" tamanho="sm" onClick={onTentarDeNovo}>
                        Tentar de novo
                      </Botao>
                    </>
                  ) : (
                    'Carregando…'
                  )}
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              <thead>
                <tr className="text-[10px] tracking-[0.04em] text-texto-fraco uppercase">
                  <th className={cn(CLASSE_DA_CELULA, 'text-left font-bold')}>Data</th>
                  <th className={cn(CLASSE_DA_CELULA, 'text-left font-bold')}>Favorecido</th>
                  <th className={cn(CLASSE_DA_CELULA, 'text-left font-bold')}>Categoria</th>
                  <th className={cn(CLASSE_DA_CELULA, 'text-right font-bold')}>Valor</th>
                  <th className={CLASSE_DA_CELULA} />
                </tr>
              </thead>
              <tbody>
                {lancamentos.map((lancamento) => (
                  <tr key={lancamento.id} className="border-t border-emb-borda text-[12px]">
                    <td className={cn(CLASSE_DA_CELULA, 'whitespace-nowrap')}>
                      {formatarData(lancamento.data)}
                    </td>
                    <td className={CLASSE_DA_CELULA}>
                      {lancamento.favorecido ?? '—'}
                      {lancamento.descricao !== '' && (
                        <div className={CLASSE_DA_SEGUNDA_LINHA}>{lancamento.descricao}</div>
                      )}
                    </td>
                    <td className={CLASSE_DA_CELULA}>
                      {lancamento.categoria === '' ? '—' : lancamento.categoria}
                      {lancamento.subcategoria !== null && (
                        <div className={CLASSE_DA_SEGUNDA_LINHA}>
                          {lancamento.subcategoria}
                        </div>
                      )}
                    </td>
                    <td
                      className={cn(
                        CLASSE_DA_CELULA,
                        'text-right tabular-nums',
                        lancamento.tipo === 'receita' ? 'text-sucesso' : 'text-foreground',
                      )}
                    >
                      {lancamento.tipo === 'receita' ? '+ ' : '- '}
                      {formatarReais(lancamento.valor)}
                    </td>
                    <td className={cn(CLASSE_DA_CELULA, 'text-right')}>
                      {podeEditar && (
                        <Botao
                          variante="perigo"
                          tamanho="sm"
                          icone
                          title="Excluir"
                          aria-label="Excluir"
                          onClick={() => void handleExcluir(lancamento.id)}
                          disabled={excluir.isPending}
                        >
                          🗑
                        </Botao>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </>
          )}
        </table>
      </CorpoDoCartao>
    </Cartao>
  );
}
